import React, { useState } from 'react'

import { FormattedMessage } from 'react-intl'

import Theme from '@theme'
import { Icon } from '@tribegroup'
import {
  IScoreBreakdown,
  IScoreBreakdownCategory,
  IScoreBreakdownFlag,
  ScoreBreakdownFlagSeverity,
} from '../types'
import {
  CategoryHeader,
  CategoryLabel,
  CategoryList,
  CategoryRow,
  CategoryScore,
  CategoryScoreCluster,
  CategoryScoreFill,
  CategoryScoreTrack,
  CategorySummary,
  FlagPill,
  FlagScheme,
  FlagsRow,
  ScoreBand,
  ScoreBreakdownContent,
  ScoreBreakdownContentInner,
  ScoreBreakdownRoot,
  ToggleButton,
} from './ScoreBreakdown.styled'

interface IProps {
  scoreBreakdown?: IScoreBreakdown | null
  flags?: IScoreBreakdownFlag[] | null
}

// Canonical BE category keys per the BRAND-9489 two-level-disclosure design,
// in the top-to-bottom order the mockup actually shows them (this differs
// from an earlier alphabetical-ish ordering that shipped ahead of the
// design — that was a bug, not a preserved convention). Anything outside
// this set is a BE-added category we don't know about yet — those are
// appended after the canonical six, in their original payload order.
const CANONICAL_CATEGORY_ORDER = [
  'audience_match',
  'brand_product_fit',
  'performance_reach',
  'risk_brand_safety',
  'content_quality_persona',
  'compliance_outreach_viability',
]

// Sentence-case labels straight from the ticket copy ("Audience match", not
// "Audience Match") — kept as-is even though the mockup's literal pixels show
// Title Case; this is a deliberate design-brief instruction, not a mockup
// transcription. Falls back to a mechanically title-cased version of the key
// for any category the BE adds later that isn't in this list yet.
const CANONICAL_CATEGORY_LABELS: Record<string, string> = {
  audience_match: 'Audience match',
  brand_product_fit: 'Brand product fit',
  compliance_outreach_viability: 'Compliance/outreach viability',
  content_quality_persona: 'Content quality/persona',
  performance_reach: 'Performance reach',
  risk_brand_safety: 'Risk/brand safety',
}

const formatLabel = (key: string): string =>
  CANONICAL_CATEGORY_LABELS[key] ??
  key
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

// Defensive clamp for the score bar's width (and, in turn, the score band
// used to colour both the bar and the printed number) — `score` is typed as
// a required number, but the BE category set is "open-ended and not fully
// validated upstream" (see IScoreBreakdownCategory), so an out-of-range or
// malformed value is plausible. This only affects the bar/band; the printed
// number is guarded separately by `formatScore` below.
const clampScore = (score: number): number => {
  if (typeof score !== 'number' || Number.isNaN(score)) return 0
  return Math.min(100, Math.max(0, score))
}

// Same "open-ended, not fully validated upstream" BE shape that motivates
// `clampScore` — a non-finite score (NaN, +/-Infinity) would otherwise
// render literally (e.g. the digit reading "NaN"), the same defect class as
// the historical weight "2000%" bug. Falls back to an em dash rather than a
// zero so a bad value reads as "unknown", not as a real (mid-band) score.
const formatScore = (score: number): string => (Number.isFinite(score) ? String(score) : '—')

const getScoreBand = (score: number): ScoreBand => {
  if (score >= 60) return 'high'
  if (score >= 40) return 'mid'
  return 'low'
}

const FLAG_SCHEME: Record<ScoreBreakdownFlagSeverity, FlagScheme> = {
  critical: 'error',
  high: 'error',
  medium: 'alert',
  low: 'default',
  info: 'default',
}

// BRAND-9590: flags are generated per-criterion by the scoring model, so the
// same code can legitimately be raised several times for one creator (the
// prompt spec tells it to flag every brief field it had to rely on with
// missing data, for instance). The pills render only the code's label, so
// repeats are visually identical and add no signal — collapse them.
//
// These two are dropped outright because BRAND-9590 names them explicitly.
// Their justifications are NOT the same, so don't collapse them in your head:
//
// - `data_incomplete` is a DataCompletenessFlag (const severity `low`). It
//   describes the completeness of the INPUT data, not the creator, so a
//   reviewer can take no action on it and the score already prices in the
//   uncertainty.
// - `no_recent_activity` is a RiskFlag (`high|medium|low`) raised for low
//   posting cadence or a format gap. That IS a statement about the creator,
//   and suppressing it hides a real signal. It is dropped here only because
//   the ticket asks for it — flagged back to product rather than silently
//   justified. Revisit if that call changes.
//
// Matched on the BE flag code (`type`), not the rendered label. Codes are
// the scoring agent's own enum (creator_scoring.schema.json: RiskFlag.code /
// DataCompletenessFlag.code) and reach us verbatim — `_translate_flags`
// renames `code` -> `type` without touching the value.
const SUPPRESSED_FLAG_TYPES = new Set(['data_incomplete', 'no_recent_activity'])

const normalizeFlagType = (type: string): string =>
  String(type ?? '')
    .trim()
    .toLowerCase()

// Criteria 5 and 6 can both raise the same RiskFlag code at DIFFERENT
// severities, so a duplicate is not always a straight repeat. Keeping the
// first occurrence's severity would let a `high` raised second be rendered
// with the muted `low` scheme — a risk surface silently understating risk,
// which is the wrong direction to fail in. Rank so dedupe can keep the
// worst severity seen for a code while leaving its position alone.
//
// The BE defines no ordering of its own (RiskFlag is `high|medium|low`,
// DataCompletenessFlag is const `low`); `critical` and `info` exist only in
// the FE type, so they are ranked defensively at the ends.
const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
  info: 0,
}

const severityRank = (severity?: string): number => SEVERITY_RANK[severity ?? ''] ?? 0

/**
 * Drops the suppressed codes and collapses repeats to one pill per distinct
 * code.
 *
 * Keeps the FIRST occurrence's POSITION and label, so the model's own
 * ordering survives, but folds in the HIGHEST severity raised for that code
 * (see SEVERITY_RANK) so deduping can never downgrade a risk signal.
 *
 * Also drops entries with no usable `type`: the pill body is
 * `formatLabel(flag.type)`, which would render an empty pill.
 */
const visibleFlags = (flags: IScoreBreakdownFlag[]): IScoreBreakdownFlag[] => {
  const kept = new Map<string, IScoreBreakdownFlag>()
  flags.forEach((flag) => {
    const type = normalizeFlagType(flag.type)
    if (!type || SUPPRESSED_FLAG_TYPES.has(type)) return

    const existing = kept.get(type)
    if (!existing) {
      kept.set(type, flag)
      return
    }
    if (severityRank(flag.severity) > severityRank(existing.severity)) {
      // Position and label come from the first occurrence; only the severity
      // is escalated.
      kept.set(type, { ...existing, severity: flag.severity })
    }
  })
  return Array.from(kept.values())
}

interface ICategoryItemProps {
  categoryKey: string
  category: IScoreBreakdownCategory
}

// Each category row owns its own open/closed state — this is deliberately
// NOT an accordion; any number of categories can be expanded at once
// (BRAND-9489 design: the mockup only shows one expanded example and doesn't
// indicate forced single-open exclusivity).
const CategoryItem: React.FC<ICategoryItemProps> = ({ categoryKey, category }) => {
  const [open, setOpen] = useState(false)
  const clampedScore = clampScore(category.score)
  const band = getScoreBand(clampedScore)
  const summaryId = `score-breakdown-category-${categoryKey}-summary`

  return (
    <CategoryRow data-testid={`score-breakdown-category-${categoryKey}`}>
      <CategoryHeader
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={summaryId}
        data-testid={`score-breakdown-category-${categoryKey}-toggle`}
      >
        <CategoryLabel>{formatLabel(categoryKey)}</CategoryLabel>
        <CategoryScoreCluster>
          <CategoryScoreTrack aria-hidden="true">
            <CategoryScoreFill $width={clampedScore} $band={band} />
          </CategoryScoreTrack>
          <CategoryScore $band={band} data-testid="score-breakdown-category-score">
            {formatScore(category.score)}
          </CategoryScore>
          <Icon glyph={open ? 'chevron-up' : 'chevron-down'} size={1} color={Theme.grey700Color} />
        </CategoryScoreCluster>
      </CategoryHeader>
      {open && category.summary && (
        <CategorySummary
          id={summaryId}
          data-testid={`score-breakdown-category-${categoryKey}-summary`}
        >
          {category.summary}
        </CategorySummary>
      )}
    </CategoryRow>
  )
}

const ScoreBreakdown: React.FC<IProps> = ({ scoreBreakdown, flags }) => {
  const [open, setOpen] = useState(false)
  // Tracks what the outer toggle's LABEL/CHEVRON should currently show. This is
  // deliberately separate from `open` (which drives aria-expanded/aria-hidden
  // and the actual max-height collapse animation): on collapse, the label must
  // keep reading "Hide breakdown" until the max-height transition genuinely
  // finishes, so it doesn't visually flip before the content has finished
  // animating shut. See handleToggleClick / handleContentTransitionEnd below.
  const [labelOpen, setLabelOpen] = useState(false)

  // The BE category/flag sets are open-ended and not fully validated upstream
  // (see IScoreBreakdown / IScoreBreakdownFlag comments), so a null or
  // non-object entry (e.g. `{ audience_match: null }`, `flags: [null]`) is
  // plausible. Drop those rather than letting a `.score`/`.severity` access
  // throw and crash the whole drawer.
  const categories = Object.entries(scoreBreakdown ?? {})
    .filter(
      (entry): entry is [string, IScoreBreakdownCategory] =>
        !!entry[1] && typeof entry[1] === 'object',
    )
    .sort(([aKey], [bKey]) => {
      const aIndex = CANONICAL_CATEGORY_ORDER.indexOf(aKey)
      const bIndex = CANONICAL_CATEGORY_ORDER.indexOf(bKey)
      if (aIndex === -1 && bIndex === -1) return 0
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1
      return aIndex - bIndex
    })
  const flagList = visibleFlags(
    (flags ?? []).filter((flag): flag is IScoreBreakdownFlag => !!flag && typeof flag === 'object'),
  )

  if (categories.length === 0 && flagList.length === 0) {
    return null
  }

  const handleToggleClick = () => {
    const nextOpen = !open
    setOpen(nextOpen)
    if (nextOpen) {
      // Expanding: flip the label immediately, same as the content.
      setLabelOpen(true)
    }
    // Collapsing: leave labelOpen alone here — handleContentTransitionEnd
    // flips it once the max-height collapse transition actually finishes.
  }

  const handleContentTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    // Only react to the max-height transition on this element itself (not a
    // bubbled transitionend from some unrelated descendant), and only flip
    // the label closed if we're still actually closed by the time it fires —
    // if the user reopened mid-collapse, `open` is back to true and this
    // stale event must be ignored so the label doesn't get incorrectly
    // flipped closed.
    if (e.target !== e.currentTarget || e.propertyName !== 'max-height') return
    if (!open) {
      setLabelOpen(false)
    }
  }

  // The content stays mounted in both states so the card can animate its
  // max-height open/closed (see ScoreBreakdownContent) — the "tucked card"
  // reveal from the BRAND-9489 mock. Collapsed, it's max-height:0 and marked
  // aria-hidden so assistive tech skips it; only the toggle strip peeks out.
  return (
    <ScoreBreakdownRoot data-testid="score-breakdown-section">
      <ScoreBreakdownContent
        id="score-breakdown-content"
        data-testid="score-breakdown-content"
        $open={open}
        aria-hidden={!open}
        onTransitionEnd={handleContentTransitionEnd}
      >
        <ScoreBreakdownContentInner>
          {flagList.length > 0 && (
            <FlagsRow data-testid="score-breakdown-flags">
              {flagList.map((flag, index) => (
                <span
                  key={`${flag.type}-${index}`}
                  data-testid={`score-breakdown-flag-${index}`}
                  data-severity={flag.severity}
                >
                  <FlagPill $scheme={FLAG_SCHEME[flag.severity] ?? 'default'}>
                    {formatLabel(flag.type)}
                  </FlagPill>
                </span>
              ))}
            </FlagsRow>
          )}

          {categories.length > 0 && (
            <CategoryList>
              {categories.map(([key, category]) => (
                <CategoryItem key={key} categoryKey={key} category={category} />
              ))}
            </CategoryList>
          )}
        </ScoreBreakdownContentInner>
      </ScoreBreakdownContent>

      <ToggleButton
        type="button"
        onClick={handleToggleClick}
        aria-expanded={open}
        aria-controls="score-breakdown-content"
        data-testid="score-breakdown-toggle"
        $open={open}
      >
        <FormattedMessage
          id={
            labelOpen
              ? 'creatorSearch.drawer.scoreBreakdown.hideBreakdown'
              : 'creatorSearch.drawer.scoreBreakdown.viewBreakdown'
          }
        />
        <Icon
          glyph={labelOpen ? 'chevron-up' : 'chevron-down'}
          size={1}
          color={Theme.primaryColor}
        />
      </ToggleButton>
    </ScoreBreakdownRoot>
  )
}

export { ScoreBreakdown }
export default ScoreBreakdown
